import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts/highstock';
import NoDataToDisplay from 'highcharts/modules/no-data-to-display';
import { useEffect, useRef, useState } from 'react';

// initialize module
import { useTheme } from '@emotion/react';
import { Card } from '@mui/material';
import { DateTime } from 'luxon';
import { darkTheme, lightTheme } from './highchartsTheme';

NoDataToDisplay(Highcharts);
const toUTC = (timestamp) =>
  DateTime.fromISO(timestamp, { zone: 'utc' }).toMillis();

const getSeverityColor = (alarm) => {
  switch (alarm.Severity) {
    case 'Information':
      return 'hsla(200, 84%, 47%, 0.2)';
    case 'Trivial':
      return 'hsla(200, 84%, 47%, 0.2)';
    case 'Minor':
      return 'hsla(33, 97%, 55%, 0.2)';
    case 'Medium':
      return 'hsla(33, 97%, 55%, 0.2)';
    case 'Major':
      return 'hsla(0, 67%, 47%, 0.2)';
    case 'Critical':
      return 'hsla(0, 67%, 47%, 0.2)';
    default:
      return 'hsla(200, 84%, 47%, 0.2)';
  }
};

const UnitAlarmChart = ({ alarm, unitData, isLoading }) => {
  const chartRef = useRef(null);
  const [isChartReady, setIsChartReady] = useState(false);

  const theme = useTheme();

  const [chartOptions, setChartOptions] = useState({
    title: { text: '' },
    chart: {
      height: 400,
      spacingLeft: 40,
      spacingRight: 40,
      navigator: { enabled: false },
      events: {
        load: function () {
          setIsChartReady(true);
        },
        destroy: function () {
          setIsChartReady(false);
        },
      },
    },
    credits: { enabled: false },
    lang: {
      noData: 'No data available for this alarm', // 👈 Message text
    },
    noData: {
      style: {
        fontWeight: 'normal',
        fontSize: '15px',
        color: 'gray',
      },
    },
    loading: {
      hideDuration: 200,
      showDuration: 100,
      labelStyle: { fontWeight: 'bold' },
      style: { backgroundColor: 'rgba(0,0,0,0.0)' }, // transparent bg, keep chart visible
    },
    time: { useUTC: true },
    legend: {
      enabled: true,
      layout: 'horizontal',
      align: 'center',
      verticalAlign: 'bottom',
    },
    rangeSelector: { enabled: false },
    xAxis: { type: 'datetime', plotLines: [] },
    series: [], // Default empty series
  });

  // Update chart options if data is available
  useEffect(() => {
    if (isChartReady && unitData && unitData.length > 0) {
      setChartOptions((prevOptions) => ({
        ...prevOptions,
        title: { text: '' },
        chart: {
          ...prevOptions.chart,
        },
        series: unitData,
        navigator: {
          enabled: false, // Keep disabled
        },

        xAxis: {
          ...prevOptions.xAxis,
          plotBands: [
            {
              color: getSeverityColor(alarm),
              from: toUTC(alarm.DataFrom),
              to: toUTC(alarm.DataTo),
              label: {
                text: `${alarm.AlarmSubtype}`,
                rotation: 0,
                y: 20,
                align: 'left',
                style: { color: theme.palette.text.primary },
              },
            },
          ],
        },
        yAxis: {
          title: { text: 'Value' },
          labels: {
            style: { color: theme.palette.text.primary },
          },
          // gridLineColor: theme.palette.divider,
          gridLineWidth: 0,
        },
      }));
    }
  }, [unitData, alarm, isChartReady, theme.palette.text.primary]);

  useEffect(() => {
    const themeOptions = theme.palette.mode === 'dark' ? darkTheme : lightTheme;
    Highcharts.setOptions(themeOptions);

    if (isChartReady && chartRef.current && chartRef.current.chart) {
      chartRef.current.chart.update({
        chart: {
          title: { text: '' },
          backgroundColor: themeOptions.chart.backgroundColor,
        },
        title: {
          style: {
            color: themeOptions.title.style.color,
          },
        },
        xAxis: {
          gridLineColor: themeOptions.xAxis.gridLineColor,
          lineColor: themeOptions.xAxis.lineColor,
          labels: {
            style: {
              color: themeOptions.xAxis.labels.style.color,
            },
          },
        },
        yAxis: [
          {
            gridLineColor: themeOptions.yAxis.gridLineColor,
            gridLineWidth: 0,
            labels: {
              style: {
                color: themeOptions.yAxis.labels.style.color,
              },
            },
          },
          {
            gridLineColor: themeOptions.yAxis.gridLineColor,
            labels: {
              style: {
                color: themeOptions.yAxis.labels.style.color,
              },
            },
          },
        ],
        tooltip: {
          backgroundColor: themeOptions.tooltip.backgroundColor,
          borderColor: themeOptions.tooltip.borderColor,
        },
      });
    }
  }, [isChartReady, theme]);

  useEffect(() => {
    const chart = chartRef.current?.chart;
    if (!chart || !isChartReady) return;

    if (isLoading) {
      // Ensure we DO NOT see the noData overlay during loading
      chart.hideNoData?.();
      chart.showLoading?.('Loading data…');
      return;
    }

    // Loading finished
    chart.hideLoading?.();

    // Decide whether to show noData now
    const hasAnyPoints = (chart.series || []).some((s) => {
      // points array exists after render; fallback to data length if needed
      const pointsCount =
        (s.points && s.points.length) ||
        (Array.isArray(s.options.data) ? s.options.data.length : 0);
      return s.visible !== false && pointsCount > 0;
    });

    if (!hasAnyPoints) {
      chart.showNoData?.(); // uses lang.noData text
    } else {
      chart.hideNoData?.();
    }
  }, [isLoading, isChartReady]);

  return (
    <Card variant="outlined" sx={{ p: 2, my: 2 }}>
      <HighchartsReact
        highcharts={Highcharts}
        options={
          chartOptions || {
            chart: { title: { text: 'No data available' } },
          }
        }
        ref={chartRef}
      />
    </Card>
  );
};

export default UnitAlarmChart;
